/**
 * ToolHeader
 *
 * Shared tool title block. Back-nav pills (formerly a separate ToolNav row
 * above the header) now live on the SAME LINE as the title: one back control
 * pins far left, an optional second pins far right, and the title stays
 * optically centred between them via a three-column grid (both side columns
 * are equal-width, so they balance regardless of label length).
 *
 * Usage:
 *   <ToolHeader leagueName={league?.name} title="Trade Analyser" subtitle="..."
 *     back={{ label: 'Go to League Home Screen', to: `/league/${id}` }} />
 *
 *   // Draft sub-pages get a second pill on the right:
 *   <ToolHeader ... back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
 *     backRight={{ label: 'Go to Draft Board', to: `/league/${id}/draft` }} />
 */

import { Link } from 'react-router-dom'
import { V2 } from './JaiV2'
import { JAI_FONTS } from './JaiBrand'
import { IS_NATIVE_APP } from '../services/runtime'
import { useMinWidth } from '../hooks/useMinWidth'

// On native, "Go to League Home Screen" as full text in a narrow grid
// column overflows onto the centred title on phone widths — the pill's
// white-space:nowrap keeps it from shrinking, so it just floats over the
// title instead. Native gets a plain icon-only back arrow for the left
// (`back`) pill instead — the one true "up" navigation this row needs.
// `backRight` is dropped entirely on native (see below) rather than given
// the same treatment: a second nav element in this row had no room to
// spare next to a wide title and wasn't reading well even before that.
// Web keeps the original full-text pill on both sides.
function NavPill({ to, children }) {
  if (IS_NATIVE_APP) {
    return (
      <Link to={to} aria-label={typeof children === 'string' ? children : 'Back'}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 34, height: 34, borderRadius: '50%',
          background: 'white', border: `1px solid ${V2.hairSoft}`,
          color: V2.mute, textDecoration: 'none', flexShrink: 0,
        }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 3 5 8l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
    )
  }
  return (
    <Link
      to={to}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 12, fontWeight: 600, color: V2.mute,
        background: 'white', border: `1px solid ${V2.hairSoft}`,
        borderRadius: 999, padding: '6px 13px',
        textDecoration: 'none', fontFamily: JAI_FONTS.tight,
        whiteSpace: 'nowrap',
        transition: 'border-color 150ms, color 150ms',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(30,91,214,0.3)'; e.currentTarget.style.color = V2.blue }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = V2.hairSoft; e.currentTarget.style.color = V2.mute }}
    >
      {children}
    </Link>
  )
}

export default function ToolHeader({ leagueName, title, subtitle, back, backRight }) {
  // On a phone the web version's two side pills have no room next to a 30px
  // title and sat on top of it. Stack instead: the pills on one line above,
  // the title underneath. (The native app already shows a lone back arrow.)
  const roomy = useMinWidth(640)
  if (!roomy && !IS_NATIVE_APP) {
    return (
      // paddingTop clears the floating "open sidebar" tab that AppShell pins to
      // the top-left of a phone-width web page; without it the tab sat on top
      // of the first pill.
      <div style={{ marginBottom: 20, paddingTop: 36 }}>
        {(back || backRight) && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {back ? <NavPill to={back.to}>{back.label}</NavPill> : <span />}
            {backRight && <NavPill to={backRight.to}>{backRight.label}</NavPill>}
          </div>
        )}
        <div style={{ textAlign: 'center' }}>
          {leagueName && (
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: V2.faint, fontFamily: JAI_FONTS.mono, marginBottom: 6 }}>
              {leagueName}
            </div>
          )}
          <h1 style={{ fontSize: 26, fontWeight: 800, color: V2.navy, letterSpacing: '-0.035em', margin: 0, lineHeight: 1.05 }}>{title}</h1>
          {subtitle && <p style={{ fontSize: 13, color: V2.mute, margin: '7px 0 0' }}>{subtitle}</p>}
        </div>
      </div>
    )
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)', alignItems: 'start', gap: 12, marginBottom: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
        {back && <NavPill to={back.to}>{back.label}</NavPill>}
      </div>

      <div style={{ textAlign: 'center' }}>
        {leagueName && (
          <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: V2.faint, fontFamily: JAI_FONTS.mono, marginBottom: 6 }}>
            {leagueName}
          </div>
        )}
        <h1 style={{ fontSize: 30, fontWeight: 800, color: V2.navy, letterSpacing: '-0.035em', margin: 0, lineHeight: 1 }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{ fontSize: 13, color: V2.mute, margin: '7px 0 0' }}>{subtitle}</p>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {/* Dropped on native: cramming a second nav element into this row
            next to a wide title left it almost no room (it was truncating
            to unreadable fragments like "Tr"), and it wasn't reading well
            even before that — a plain back arrow to League Home is the one
            navigation this row needs to earn its keep. Web keeps the pill. */}
        {backRight && !IS_NATIVE_APP && <NavPill to={backRight.to}>{backRight.label}</NavPill>}
      </div>
    </div>
  )
}
